/**
 * Application Contexts — la source unique de vérité des "pages logiques".
 *
 * Chaque contexte = un identifiant string que l'URL produit (via
 * `route.handle.context` dans React Router) et qui pilote ce que le shell rend
 * dans les 3 panneaux + le header.
 *
 * Le `featureRegistry` traduit ce contexte en `tabs / centerView / headerSlot`
 * via les `manifest.ts` des features actives.
 *
 * @example
 * ```tsx
 * // Dans une route :
 * { path: '/skill-up/session/:id', handle: { context: CONTEXTS.SKILL_UP_SESSION } }
 *
 * // Dans un manifest :
 * contexts: [CONTEXTS.SKILL_UP_HOME, CONTEXTS.SKILL_UP_SESSION] as const,
 * ```
 *
 * @remarks
 * Les contextes sont **ajoutés au fur et à mesure** que les features sont
 * implémentées. Pour la Phase 1 (contrat seul), on déclare uniquement le
 * minimum pour que les futures features (skill-up, auth, settings) puissent
 * s'enregistrer sans avoir à modifier ce fichier d'abord.
 *
 * Quand on migre tech-tests, admin, candidate-results en Phase 5, on étend
 * cette constante.
 */
export const CONTEXTS = {
  // ── Auth ────────────────────────────────────────────────────────────────
  AUTH_LOGIN: 'auth-login',

  // ── Settings ────────────────────────────────────────────────────────────
  SETTINGS: 'settings',

  // ── Skill-up (feature témoin V2) ────────────────────────────────────────
  SKILL_UP_HOME: 'skill-up-home',
  SKILL_UP_SESSION: 'skill-up-session',
  SKILL_UP_COMPETENCES: 'skill-up-competences',

  // ── Tech tests (Phase 5.3) ──────────────────────────────────────────────
  TECH_TESTS_CATALOG: 'tech-tests-catalog',
  TECH_TESTS: 'tech-tests',
  TECH_TEST_EVALUATION: 'tech-test-evaluation',

  // ── Candidate results (Phase 5.4) ───────────────────────────────────────
  CANDIDATE_RESULTS: 'candidate-results',
  TECH_TEST_RESULTS: 'tech-test-results',

  // ── Profile ─────────────────────────────────────────────────────────────
  PROFILE: 'profile',
  PROFILE_EDIT: 'profile-edit',

  // ── CV (liste + CV d'adéquation + grille de compétences) ──────────────
  CV_LIST: 'cv-list',
  CV: 'cv',
  CV_SKILLS: 'cv-skills',

  // ── Referral (SAAS-704 — wallet parrainage) ───────────────────────────────
  REFERRAL: 'referral',

  // ── Purchases ───────────────────────────────────────────────────────────
  PURCHASES: 'purchases',
  PURCHASES_PACK_EVAL: 'purchases-pack-eval',
  PURCHASES_COMPLETE: 'purchases-complete',
  PURCHASES_HISTORY: 'purchases-history',
  PAYMENT_SUCCESS: 'payment-success',
  PAYMENT_CANCEL: 'payment-cancel',

  // ── Admin (Phase 5.5) ───────────────────────────────────────────────────
  ADMIN_EVALUATIONS: 'admin-evaluations',
  ADMIN_EVALUATION_FORM: 'admin-evaluation-form',
  ADMIN_EVALUATION_VARIANT_FORM: 'admin-evaluation-variant-form',
  ADMIN_STEPS_LIBRARY: 'admin-steps-library',
  ADMIN_STEP_FORM: 'admin-step-form',
  ADMIN_RULES_LIBRARY: 'admin-rules-library',
  ADMIN_CHECKBOXES_LIBRARY: 'admin-checkboxes-library',
  ADMIN_CHECKBOX_FORM: 'admin-checkbox-form',

  // ── Admin — Users (SAAS-148) ────────────────────────────────────────────
  ADMIN_USERS: 'admin-users',
  ADMIN_USER_FORM: 'admin-user-form',

  // ── Admin — Tests techniques (SAAS-149) ─────────────────────────────────
  ADMIN_TESTS: 'admin-tests',
  ADMIN_TEST_FORM: 'admin-test-form',

  // ── Admin — User-Tests / Assignations (SAAS-150) ─────────────────────────
  ADMIN_USER_TESTS: 'admin-user-tests',
  ADMIN_USER_TEST_FORM: 'admin-user-test-form',
  ADMIN_USER_TEST_DETAIL: 'admin-user-test-detail',

  // ── Admin — Codes promo (SAAS-610) ───────────────────────────────────────
  ADMIN_PROMO_CODES: 'admin-promo-codes',
  ADMIN_PROMO_CODE_FORM: 'admin-promo-code-form',

  // ── Admin — Compétences / Skills (SAAS-A-147) ────────────────────────────
  ADMIN_SKILLS: 'admin-skills',
  ADMIN_SKILL_FORM: 'admin-skill-form',

  // ── Admin — Groupes de compétences / Skill-Groups (SAAS-A-167) ────────────
  ADMIN_SKILL_GROUPS: 'admin-skill-groups',
  ADMIN_SKILL_GROUP_FORM: 'admin-skill-group-form',
  // Création : contexte distinct de l'édition — pas de panneau droit
  // « Nouvelle compétence » ici (création de skill interdite au create).
  ADMIN_SKILL_GROUP_CREATE: 'admin-skill-group-create',

  // ── Admin — Grilles d'évaluation / Skill-Grids (SAAS-A-172) ───────────────
  ADMIN_SKILL_GRIDS: 'admin-skill-grids',
  // Création et édition partagent ce contexte (même panneau « Ajouter une checkbox »).
  ADMIN_SKILL_GRID_FORM: 'admin-skill-grid-form',

  // ── Admin — Postes / Positions (SAAS-A-174) ──────────────────────────────
  ADMIN_POSITIONS: 'admin-positions',
  ADMIN_POSITION_FORM: 'admin-position-form',
  // Création : contexte distinct de l'édition — le panneau « Lier un groupe »
  // n'est ouvert qu'en édition (le body de création ne porte pas de groupes).
  ADMIN_POSITION_CREATE: 'admin-position-create',

  // ── Admin — LLM Config (SAAS-288 → 292 : fragments / presets ; SAAS-995 :
  // le template disparaît, replié dans le preset — ADR SAAS-A-184 D1) ───────
  ADMIN_LLM_FRAGMENTS: 'admin-llm-fragments',
  ADMIN_LLM_FRAGMENT_FORM: 'admin-llm-fragment-form',
  ADMIN_LLM_PRESETS: 'admin-llm-presets',
  ADMIN_LLM_PRESET_FORM: 'admin-llm-preset-form',

  // ── Admin — Skill-Up authoring (SAAS-827 : nœuds / concepts / roadmap) ────
  ADMIN_SKILL_UP_NODES: 'admin-skill-up-nodes',
  ADMIN_SKILL_UP_NODE_FORM: 'admin-skill-up-node-form',
  // Liaisons : page dédiée — le câblage d'un nœud (graphe local + éditeur
  // d'arêtes), pendant nœud-centrique de la roadmap côté concept.
  ADMIN_SKILL_UP_NODE_LINKS: 'admin-skill-up-node-links',
  ADMIN_SKILL_UP_CONCEPTS: 'admin-skill-up-concepts',
  ADMIN_SKILL_UP_CONCEPT_FORM: 'admin-skill-up-concept-form',
  // Roadmap : contexte dédié — la timeline d'un concept n'est ni une liste ni
  // un formulaire, elle a son propre centerView.
  ADMIN_SKILL_UP_CONCEPT_ROADMAP: 'admin-skill-up-concept-roadmap',

  // ── Admin — Tableau de bord (SAAS-1219) ──────────────────────────────────
  ADMIN_DASHBOARD: 'admin-dashboard',
} as const;

/**
 * Union type des identifiants de contexte applicatif.
 *
 * Utilise `as const` pour figer les valeurs en types littéraux, ce qui permet :
 * - Auto-complétion dans les manifests
 * - `tsc` détecte une référence à un contexte qui n'existe pas
 * - `keyof typeof CONTEXTS` pour la sécurité côté code consommateur
 */
export type Context = (typeof CONTEXTS)[keyof typeof CONTEXTS];

/**
 * Tous les contextes déclarés.
 */
export const ALL_CONTEXTS: readonly Context[] = Object.values(CONTEXTS);

/**
 * Contextes qui ne sont **pas** rendus par le shell, et n'ont donc pas à
 * fournir de `centerView`.
 *
 * @remarks
 * Une exemption se déclare ici, avec sa raison — nulle part ailleurs. C'est
 * ce qui permet à `SHELL_RENDERED_CONTEXTS` de se dériver au lieu d'être
 * recopié : la liste passée au registry ne peut plus prendre du retard sur
 * `CONTEXTS`, puisqu'il n'y a plus de liste à tenir.
 */
const SHELL_EXEMPT_CONTEXTS: readonly Context[] = [
  // Les routes d'authentification vivent dans la zone `'auth'` : elles sont
  // rendues sous `AuthLayout`, pas par les panneaux du shell.
  CONTEXTS.AUTH_LOGIN,
];

/**
 * Les contextes que le registry doit voir couverts au boot — c'est-à-dire
 * pour lesquels au moins une feature déclare un `centerView`.
 *
 * @remarks
 * Cette liste était auparavant **recopiée à la main** dans
 * `features-registration.ts`, alors que la JSDoc de `markInitialized`
 * recommandait déjà `ALL_CONTEXTS`. Elle avait pris du retard : elle en
 * comptait 56 pour 58 déclarés, et `ADMIN_CHECKBOX_FORM` échappait à la
 * validation — sans conséquence, il se trouve qu'il a bien un `centerView`,
 * mais personne ne l'avait vérifié.
 *
 * Un contexte ajouté sans vue n'aurait alors pas fait échouer le boot : il
 * serait tombé, en production, sur le placeholder de développement
 * « Pas encore de feature pour ce contexte ».
 */
export const SHELL_RENDERED_CONTEXTS: readonly Context[] = ALL_CONTEXTS.filter(
  (context) => !SHELL_EXEMPT_CONTEXTS.includes(context),
);
